/**
 * managerReviewApi — proxy calls for the Manager Review tool.
 *
 * GET /api/people/manager-review/employees
 *   Returns the direct reports of the logged-in manager.
 *   Also acts as the access gate: 403 if the caller has no direct reports.
 */
import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';
import type { Employee } from '../types/managerReview';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-520260363689.europe-west3.run.app';

export interface FetchEmployeesResult {
  employees: Employee[];
}

export class PeopleDatabaseNotConfiguredError extends Error {
  constructor() {
    super('People database not configured on the server.');
    this.name = 'PeopleDatabaseNotConfiguredError';
  }
}

export class NotAManagerError extends Error {
  details: string;
  constructor(details?: string) {
    super('You do not have any direct reports in the People database.');
    this.name = 'NotAManagerError';
    this.details = details || '';
  }
}

/**
 * Fetch the direct reports of the currently authenticated manager.
 * Throws NotAManagerError (403) or PeopleDatabaseNotConfiguredError (503)
 * on expected failure conditions.
 */
export async function fetchDirectReports(): Promise<FetchEmployeesResult> {
  const res = await authenticatedFetch(
    `${PROXY_URL}/api/people/manager-review/employees`,
  );

  if (res.status === 403) {
    const body = await res.json().catch(() => ({}));
    throw new NotAManagerError(body?.details);
  }

  if (res.status === 503) {
    throw new PeopleDatabaseNotConfiguredError();
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error || `Server error ${res.status}`);
  }

  return res.json() as Promise<FetchEmployeesResult>;
}
